import React, { useCallback, useEffect, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Divider,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Spinner,
  Stack,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tooltip,
  Tr,
  useColorMode,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTrash, faRotateRight, faPaperPlane, faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons";
import moment from "moment";
import { cqAPI } from "../../lib/Auth";
import "./Outreach.css";

// Outreach campaigns: platform drip sequences (email / SMS / AI voice call) to
// prospect practices (no Usage rows) or existing customers. Superadmin only.
// A call step is not a script: the outreach voice agent (xAI, separate from Ezra) is
// given a goal and guidelines and holds a natural conversation toward them.
// The lifecycle the UI enforces mirrors the API's: draft -> enroll -> dry run ->
// running <-> paused -> done. Steps freeze once a campaign has left draft.
// A dry run is recorded against the current steps (campaign.dry_run_current); the
// API refuses to start a draft without one, unless the audience is a single
// hand-picked practice.
//
// "Send to one" is the way to test a campaign for real: pick a single prospect or
// customer and the first step goes to them now, through the engine itself; the rest
// of the sequence follows only while the campaign is running.

const STATUS_COLORS = { draft: "gray", running: "green", paused: "yellow", done: "blue" };
const CALL_VOICES = ["ara", "rex", "eve", "leo", "sal"];
const CALL_TARGET_LABELS = { contact: "the contact's own phone", office: "the practice's front-desk line" };
const MERGE_HINT = "{{FirstName}} {{PracticeName}} {{PMS}} {{BookingLink}}";
// Per-step metrics columns, in display order: key in metrics.by_step[step] -> header.
const STEP_METRIC_COLUMNS = [
  ["sent", "Sent"], ["delivered", "Delivered"], ["failed", "Failed"], ["skipped", "Skipped"],
  ["suppressed", "Suppressed"], ["replied", "Replied"], ["opted_out", "Opted out"], ["converted", "Converted"],
];
const STATUS_METRIC_KEYS = ["active", "replied", "converted", "opted_out", "completed", "paused", "failed"];
const DRY_RUN_TOOLTIP = "Run a dry run of the current steps first";
// The platform's sending identity (GET/PUT /outreach/settings), edited in the
// "Sending identity…" modal. A field the document leaves empty inherits an OUTREACH_*
// environment variable; the API reports which via `stored[field]`.
const IDENTITY_FIELDS = [
  {
    key: "messaging_service_sid",
    label: "Twilio Messaging Service SID",
    placeholder: "MG…",
    help: "The outreach number's own Messaging Service with its marketing A2P campaign. Never the shared practice service.",
  },
  {
    key: "phone_number",
    label: "Outreach phone number",
    placeholder: "+1 555 555 0100",
    help: "The platform's own number: caller id for AI calls, and the number pulse recognises inbound texts and status callbacks on. Its Twilio webhooks must point at pulse /outreach/incoming_text, /outreach/sms_status, /outreach/call_twiml, /outreach/call_status.",
  },
  {
    key: "postal_address",
    label: "Postal address (email footer)",
    placeholder: "123 Main St, Suite 100, City, ST 00000",
    maxLength: 300,
    help: "Required on every outreach email by CAN-SPAM. Email stays off until this is set.",
  },
  {
    key: "ses_config_set",
    label: "SES configuration set (optional)",
    placeholder: "",
    help: "Only if SES-side tagging is wanted; feedback isolation does not depend on it.",
  },
];
const emptyIdentityForm = () => Object.fromEntries(IDENTITY_FIELDS.map(({ key }) => [key, ""]));

const emptyCampaign = () => ({
  name: "",
  audience: { type: "prospects", exclude_partner_owned: true, exclude_inactive: true, require_phone: false, pms: [] },
  steps: [{ delay: 0, unit: "days", channel: "email", subject: "", body: "" }],
  quiet_hours: { start: "08:00", end: "21:00", disabled: false },
  frequency_cap: { max: 0, days: 7 },
  stop_on_reply: true,
  stop_on_convert: true,
  booking_link: "",
  call_voice: "ara",
});

const StepEditor = ({ steps, onChange, disabled }) => {
  const update = (i, patch) => onChange(steps.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));
  const remove = (i) => onChange(steps.filter((_, idx) => idx !== i));
  const add = () => onChange([...steps, { delay: 3, unit: "days", channel: "email", subject: "", body: "" }]);

  return (
    <Stack spacing={3}>
      {steps.map((step, i) => (
        <Box key={i} borderWidth="1px" borderRadius="md" p={3}>
          <Flex gap={2} wrap="wrap" align="center" mb={2}>
            <Badge>step {i + 1}</Badge>
            <Input size="sm" width="80px" type="number" min={0} value={step.delay} isDisabled={disabled}
              onChange={(e) => update(i, { delay: Number(e.target.value) })} />
            <Select size="sm" width="110px" value={step.unit} isDisabled={disabled} onChange={(e) => update(i, { unit: e.target.value })}>
              <option value="minutes">minutes</option>
              <option value="hours">hours</option>
              <option value="days">days</option>
            </Select>
            <Text fontSize="sm">after {i === 0 ? "enrollment" : `step ${i}`}, by</Text>
            <Select size="sm" width="130px" value={step.channel} isDisabled={disabled}
              onChange={(e) => update(i, e.target.value === "call"
                ? { channel: "call", call_target: step.call_target || "contact", goal: step.goal || "", guidelines: step.guidelines || "" }
                : { channel: e.target.value })}>
              <option value="email">email</option>
              <option value="sms">SMS</option>
              <option value="call">AI call</option>
            </Select>
            <IconButton aria-label="Remove step" size="sm" variant="ghost" icon={<FontAwesomeIcon icon={faTrash} />}
              isDisabled={disabled || steps.length === 1} onClick={() => remove(i)} />
          </Flex>
          {step.channel === "email" && (
            <Input size="sm" mb={2} placeholder="Subject — merge fields: {{FirstName}} {{PracticeName}} {{PMS}} {{BookingLink}}"
              value={step.subject || ""} isDisabled={disabled} onChange={(e) => update(i, { subject: e.target.value })} />
          )}
          {step.channel === "call" ? (
            <Stack spacing={2}>
              <Flex gap={2} align="center" wrap="wrap">
                <Text fontSize="sm">Call</Text>
                <Select size="sm" width="260px" value={step.call_target || "contact"} isDisabled={disabled}
                  onChange={(e) => update(i, { call_target: e.target.value })}>
                  {Object.entries(CALL_TARGET_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </Select>
                <Text fontSize="xs" color="gray.500">
                  {step.call_target === "office"
                    ? "The agent asks the front desk for the contact by name and never pitches the receptionist."
                    : "The number the contact gave us at demo sign-up."}
                </Text>
              </Flex>
              <Textarea
                size="sm"
                rows={3}
                placeholder={`Goal — what a good call achieves, e.g. "Find out whether {{FirstName}} is still evaluating an AI front desk, answer open questions, and offer to book a follow-up call." Merge fields: ${MERGE_HINT}`}
                value={step.goal || ""}
                isDisabled={disabled}
                onChange={(e) => update(i, { goal: e.target.value })}
              />
              <Textarea
                size="sm"
                rows={4}
                placeholder={`Guidelines (optional) — what to mention, what never to say, tone, facts the agent may use. e.g. "We now integrate with {{PMS}}. Do not quote prices. If they mention a competitor, ask what they like about it." Merge fields: ${MERGE_HINT}`}
                value={step.guidelines || ""}
                isDisabled={disabled}
                onChange={(e) => update(i, { guidelines: e.target.value })}
              />
              <Text fontSize="xs" color="gray.500" textAlign="left">
                No script. The voice agent introduces itself as Viva AI&apos;s assistant, converses naturally toward the goal,
                can text the booking link, records a do-not-call the moment it is asked, leaves a short voicemail if a machine
                answers, and ends the call itself with an outcome and summary you will see on the thread.
              </Text>
            </Stack>
          ) : (
            <Textarea
              size="sm"
              rows={step.channel === "email" ? 5 : 3}
              placeholder={`Message body — merge fields: ${MERGE_HINT}`}
              value={step.body || ""}
              isDisabled={disabled}
              onChange={(e) => update(i, { body: e.target.value })}
            />
          )}
        </Box>
      ))}
      <Button size="sm" variant="outline" leftIcon={<FontAwesomeIcon icon={faPlus} />} isDisabled={disabled} onClick={add} alignSelf="flex-start">
        Add step
      </Button>
    </Stack>
  );
};

function OutreachCampaigns() {
  const toast = useToast();
  const { colorMode } = useColorMode();
  const mutedColor = colorMode === "light" ? "gray.600" : "gray.400";

  const [campaigns, setCampaigns] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editing, setEditing] = useState(null);       // campaign being edited/created
  const [preview, setPreview] = useState(null);       // audience preview result
  const [sendStatus, setSendStatus] = useState(null); // platform sending identity
  const [dryRun, setDryRun] = useState(null);         // rendered messages
  const [busy, setBusy] = useState("");
  const { isOpen, onOpen, onClose } = useDisclosure();
  const dryRunModal = useDisclosure();

  // Per-step metrics: the campaign (with metrics.by_status / by_step) being shown.
  const metricsModal = useDisclosure();
  const [metrics, setMetrics] = useState(null);

  // "Send to one": a campaign, a search, and its matches.
  const sendOneModal = useDisclosure();
  const [sendOneCampaign, setSendOneCampaign] = useState(null);
  const [contactQuery, setContactQuery] = useState("");
  const [contacts, setContacts] = useState(null);
  const [searching, setSearching] = useState(false);
  const [lastSend, setLastSend] = useState(null);      // the most recent single-send result

  // The editor's single-practice audience picker (audience type "single").
  const [singleQuery, setSingleQuery] = useState("");
  const [singleMatches, setSingleMatches] = useState(null);
  const [singleSearching, setSingleSearching] = useState(false);

  const fetchCampaigns = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await cqAPI.get("/outreach/campaigns?bid=");
      if (response?.type === "ok") setCampaigns(response.data || []);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { fetchCampaigns(); }, [fetchCampaigns]);

  // Read-only: where outreach sends from, and whether each channel is wired for
  // this stage. Answers "how is the platform number assigned?" at a glance.
  // Re-run after the sending identity is saved so the strip reflects it.
  const loadStatus = useCallback(async () => {
    try {
      const response = await cqAPI.get("/outreach/status?bid=");
      if (response?.type === "ok") setSendStatus(response.data);
    } catch (e) { /* non-fatal — the strip just hides */ }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  // The sending identity editor: the server payload (values, stored flags, channel
  // state) and the four editable fields, kept apart so the "from environment" badges
  // keep describing what is in effect while the superadmin types.
  const identityModal = useDisclosure();
  const [identity, setIdentity] = useState(null);
  const [identityForm, setIdentityForm] = useState(emptyIdentityForm());
  const [identityLoading, setIdentityLoading] = useState(false);

  const applyIdentity = (data) => {
    setIdentity(data);
    setIdentityForm(Object.fromEntries(IDENTITY_FIELDS.map(({ key }) => [key, data?.[key] || ""])));
  };

  const loadIdentity = async () => {
    setIdentityLoading(true);
    try {
      const response = await cqAPI.get("/outreach/settings?bid=");
      if (response?.type === "ok") applyIdentity(response.data);
      else toast({ title: "Settings unavailable", description: response?.msg, status: "error", duration: 4000 });
    } finally {
      setIdentityLoading(false);
    }
  };

  const openIdentity = () => {
    setIdentity(null);
    setIdentityForm(emptyIdentityForm());
    identityModal.onOpen();
    loadIdentity();
  };

  const saveIdentity = async () => {
    setBusy("identity");
    try {
      const response = await cqAPI.put("/outreach/settings", identityForm);
      if (response?.type !== "ok") {
        toast({ title: "Not saved", description: response?.msg, status: "error", duration: 5000, isClosable: true });
        return;
      }
      applyIdentity(response.data);
      toast({ title: "Sending identity saved", status: "success", duration: 2000 });
      await loadStatus();
    } finally {
      setBusy("");
    }
  };

  const openEditor = (campaign) => {
    setPreview(null);
    setSingleMatches(null);
    setSingleQuery("");
    setEditing(campaign ? JSON.parse(JSON.stringify(campaign)) : emptyCampaign());
    onOpen();
  };

  const saveCampaign = async () => {
    setBusy("save");
    try {
      const response = editing._id
        ? await cqAPI.put(`/outreach/campaigns/${editing._id}`, editing)
        : await cqAPI.post("/outreach/campaigns", editing);
      if (response?.type !== "ok") {
        toast({ title: "Not saved", description: response?.msg, status: "error", duration: 5000, isClosable: true });
        return;
      }
      setEditing(response.data);
      toast({ title: "Saved", status: "success", duration: 2000 });
      await fetchCampaigns();
    } finally {
      setBusy("");
    }
  };

  const previewAudience = async () => {
    setBusy("preview");
    try {
      const response = await cqAPI.post("/outreach/audience/preview", { audience: editing.audience });
      if (response?.type === "ok") setPreview(response.data);
      else toast({ title: "Preview failed", description: response?.msg, status: "error", duration: 4000 });
    } finally {
      setBusy("");
    }
  };

  const openSendOne = (campaign) => {
    setSendOneCampaign(campaign);
    setContactQuery("");
    setContacts(null);
    setLastSend(null);
    sendOneModal.onOpen();
  };

  const searchContacts = async () => {
    const q = contactQuery.trim();
    if (q.length < 2) return;
    setSearching(true);
    try {
      const response = await cqAPI.get(`/outreach/contacts?bid=&q=${encodeURIComponent(q)}&campaign=${sendOneCampaign?._id || ""}`);
      if (response?.type === "ok") setContacts(response.data || []);
      else toast({ title: "Search failed", description: response?.msg, status: "error", duration: 4000 });
    } finally {
      setSearching(false);
    }
  };

  // The single-practice audience picker inside the editor.
  const searchSingle = async () => {
    const q = singleQuery.trim();
    if (q.length < 2) return;
    setSingleSearching(true);
    try {
      const response = await cqAPI.get(`/outreach/contacts?bid=&q=${encodeURIComponent(q)}`);
      if (response?.type === "ok") setSingleMatches(response.data || []);
      else toast({ title: "Search failed", description: response?.msg, status: "error", duration: 4000 });
    } finally {
      setSingleSearching(false);
    }
  };
  const pickSingle = (contact) => {
    setEditing({
      ...editing,
      audience: {
        ...editing.audience,
        type: "single",
        single_bid: contact.bid,
        single_snapshot: { business_name: contact.business_name, contact_name: contact.contact_name, email: contact.email, kind: contact.kind },
      },
    });
    setSingleMatches(null);
    setSingleQuery("");
  };

  const sendToContact = async (contact, restart = false) => {
    if (!sendOneCampaign) return;
    setBusy(`sendone:${contact.bid}`);
    try {
      const response = await cqAPI.post(`/outreach/campaigns/${sendOneCampaign._id}/send-one`, { bid: contact.bid, restart });
      if (response?.type !== "ok") {
        toast({ title: "Not sent", description: response?.msg, status: "error", duration: 6000, isClosable: true });
        return;
      }
      setLastSend({ contact, ...response.data });
      if (response.data.already_enrolled) {
        toast({
          title: "Already enrolled",
          description: `${contact.business_name} is already in this campaign (${response.data.enrollment.status}). Use “Restart & send” to start their sequence over.`,
          status: "warning", duration: 7000, isClosable: true,
        });
      } else {
        const step = response.data.step;
        const ok = step && step.result === "sent";
        toast({
          title: ok ? "Sent" : `Step 1 ${step ? step.result : response.data.outcome}`,
          description: `${contact.business_name} (${response.data.kind}) · step 1 by ${step ? step.channel : "?"}${step && step.detail ? ` — ${step.detail}` : ""}${response.data.note ? ` · ${response.data.note}` : ""}`,
          status: ok ? "success" : "warning", duration: 8000, isClosable: true,
        });
      }
      await searchContacts();
      await fetchCampaigns();
    } finally {
      setBusy("");
    }
  };

  const openMetrics = async (campaign) => {
    setBusy(`metrics:${campaign._id}`);
    try {
      const response = await cqAPI.get(`/outreach/campaigns/${campaign._id}?bid=`);
      if (response?.type !== "ok") {
        toast({ title: "Metrics unavailable", description: response?.msg, status: "error", duration: 4000 });
        return;
      }
      setMetrics(response.data);
      metricsModal.onOpen();
    } finally {
      setBusy("");
    }
  };

  // Draft campaigns need a dry run of their current steps before Start; a single
  // hand-picked practice is exempt (mirrors the API rule).
  const needsDryRun = (campaign) => !campaign.dry_run_current && campaign.audience?.type !== "single";

  const act = async (campaign, action) => {
    setBusy(`${action}:${campaign._id}`);
    try {
      let response;
      if (action === "enroll") response = await cqAPI.post(`/outreach/campaigns/${campaign._id}/enroll`, {});
      else if (action === "dryrun") response = await cqAPI.get(`/outreach/campaigns/${campaign._id}/dryrun?bid=`);
      else if (action === "test") response = await cqAPI.post(`/outreach/campaigns/${campaign._id}/test`, {});
      else response = await cqAPI.put(`/outreach/campaigns/${campaign._id}/status`, { status: action });

      if (response?.type !== "ok") {
        toast({ title: "Refused", description: response?.msg, status: "error", duration: 5000, isClosable: true });
        return;
      }
      if (action === "enroll") {
        toast({
          title: "Audience enrolled",
          description: `${response.data.inserted} enrolled, ${response.data.skipped} skipped, audience ${response.data.audience_count}.`,
          status: "success", duration: 6000, isClosable: true,
        });
      } else if (action === "dryrun") {
        setDryRun(response.data);
        dryRunModal.onOpen();
      } else if (action === "test") {
        const sent = (response.data || []).filter((r) => r.sent).length;
        const calls = (response.data || []).filter((r) => r.channel === "call" && r.sent).length;
        toast({
          title: "Test sent",
          description: `${sent}/${(response.data || []).length} steps delivered to you.${calls ? ` ${calls} rehearsal call${calls === 1 ? "" : "s"} placed to your phone — the voice agent runs the step with you as the contact.` : ""}`,
          status: "info", duration: 8000, isClosable: true,
        });
      }
      await fetchCampaigns();
    } finally {
      setBusy("");
    }
  };

  return (
    <Box className="outreach-page page-pad" p={4} overflowY="auto" maxHeight="100vh" width="100%">
      <Flex justify="space-between" align="center" mb={4} wrap="wrap" gap={2}>
        <Text fontSize="xl" fontWeight="bold">Outreach</Text>
        <Flex gap={2} wrap="wrap">
          <Button size="sm" variant="outline" onClick={openIdentity}>Sending identity…</Button>
          <Button size="sm" leftIcon={<FontAwesomeIcon icon={faRotateRight} />} onClick={fetchCampaigns} isLoading={isLoading}>Refresh</Button>
          <Button size="sm" colorScheme="teal" leftIcon={<FontAwesomeIcon icon={faPlus} />} onClick={() => openEditor(null)}>New campaign</Button>
        </Flex>
      </Flex>

      <Text fontSize="sm" color={mutedColor} mb={2} textAlign="left">
        Drip sequences from the platform to practices — prospects who demoed and never signed, or existing
        customers. The lifecycle is deliberate: enroll the audience, read the dry run, then start.
      </Text>
      {sendStatus && (
        <Flex gap={2} mb={4} wrap="wrap" fontSize="xs" align="center">
          <Text color={mutedColor}>Sending as the platform ({sendStatus.stage}):</Text>
          <Badge colorScheme={sendStatus.sms?.enabled ? "green" : "red"}>SMS {sendStatus.sms?.enabled ? sendStatus.sms.messaging_service_sid : "not configured"}</Badge>
          <Badge colorScheme={sendStatus.call?.enabled ? "green" : "red"}>
            AI calls {sendStatus.call?.enabled ? sendStatus.call.from : (sendStatus.call?.voice_agent ? "no number" : "voice agent key missing")}
          </Badge>
          <Badge colorScheme={sendStatus.email?.enabled ? "green" : "red"}>Email {sendStatus.email?.enabled ? sendStatus.email.domain : "not configured"}</Badge>
          {sendStatus.a2p && (
            <Badge colorScheme={sendStatus.a2p.status === "VERIFIED" ? "green" : "yellow"}>
              A2P {sendStatus.a2p.registered === false ? "unregistered" : (sendStatus.a2p.status || "unknown")}
            </Badge>
          )}
          {sendStatus.sms?.gate && !sendStatus.sms.gate.ok && (
            <Badge colorScheme="red" whiteSpace="normal">SMS blocked: {sendStatus.sms.gate.reason || "not allowed on this stage"}</Badge>
          )}
        </Flex>
      )}

      {isLoading ? (
        <Flex justify="center" py={10}><Spinner size="xl" color="blue.500" /></Flex>
      ) : (
        <TableContainer borderWidth="1px" borderRadius="lg">
          <Table variant="simple" size="sm">
            <Thead>
              <Tr><Th>Status</Th><Th>Name</Th><Th>Audience</Th><Th>Steps</Th><Th>Enrollments</Th><Th>Created</Th><Th>Actions</Th></Tr>
            </Thead>
            <Tbody>
              {campaigns.length === 0 ? (
                <Tr><Td colSpan={7}><Text fontSize="sm" color={mutedColor}>No campaigns yet.</Text></Td></Tr>
              ) : campaigns.map((campaign) => {
                const counts = campaign.enrollment_counts || {};
                const enrolled = Object.values(counts).reduce((a, b) => a + b, 0);
                const startBlocked = campaign.status === "draft" && needsDryRun(campaign);
                return (
                  <Tr key={campaign._id}>
                    <Td>
                      <Stack spacing={1} align="flex-start">
                        <Badge colorScheme={STATUS_COLORS[campaign.status] || "gray"}>{campaign.status}</Badge>
                        {campaign.dry_run_current ? (
                          <Badge colorScheme="green" variant="subtle" fontSize="2xs">
                            dry run ✓{campaign.dry_run?.at ? ` ${moment(campaign.dry_run.at).format("MMM D")}` : ""}
                          </Badge>
                        ) : ["draft", "paused"].includes(campaign.status) && needsDryRun(campaign) ? (
                          <Badge colorScheme="orange" variant="subtle" fontSize="2xs">dry run needed</Badge>
                        ) : null}
                      </Stack>
                    </Td>
                    <Td maxW="220px" overflow="hidden" textOverflow="ellipsis">
                      <Text as="span" _hover={{ textDecoration: "underline", cursor: "pointer" }} onClick={() => openEditor(campaign)}>
                        {campaign.name}
                      </Text>
                    </Td>
                    <Td>
                      {campaign.audience?.type === "single"
                        ? `one practice: ${campaign.audience.single_snapshot?.business_name || "?"} (${campaign.audience.single_snapshot?.kind || "?"})`
                        : (campaign.audience?.type || "—")}
                    </Td>
                    <Td>{(campaign.steps || []).length}</Td>
                    <Td>
                      {enrolled
                        ? `${enrolled} (${Object.entries(counts).map(([k, v]) => `${v} ${k}`).join(", ")})`
                        : "—"}
                    </Td>
                    <Td>{moment(campaign.created).format("MMM D")}</Td>
                    <Td>
                      <Flex gap={1} wrap="wrap">
                        <Button size="xs" onClick={() => act(campaign, "enroll")}
                          isLoading={busy === `enroll:${campaign._id}`} isDisabled={campaign.status === "done"}>Enroll</Button>
                        <Button size="xs" onClick={() => act(campaign, "dryrun")}
                          isLoading={busy === `dryrun:${campaign._id}`}>Dry run</Button>
                        <Button size="xs" onClick={() => act(campaign, "test")}
                          isLoading={busy === `test:${campaign._id}`}>Test me</Button>
                        <Button size="xs" leftIcon={<FontAwesomeIcon icon={faPaperPlane} />} onClick={() => openSendOne(campaign)}
                          isDisabled={campaign.status === "done"}>Send to one…</Button>
                        {enrolled > 0 && (
                          <Button size="xs" variant="outline" onClick={() => openMetrics(campaign)}
                            isLoading={busy === `metrics:${campaign._id}`}>Metrics</Button>
                        )}
                        {campaign.status === "running" ? (
                          <Button size="xs" colorScheme="yellow" onClick={() => act(campaign, "paused")}
                            isLoading={busy === `paused:${campaign._id}`}>Pause</Button>
                        ) : (
                          // A disabled button swallows pointer events, so the tooltip wraps it.
                          <Tooltip label={DRY_RUN_TOOLTIP} isDisabled={!startBlocked} shouldWrapChildren hasArrow>
                            <Button size="xs" colorScheme="green" onClick={() => act(campaign, "running")}
                              isLoading={busy === `running:${campaign._id}`}
                              isDisabled={campaign.status === "done" || startBlocked}>Start</Button>
                          </Tooltip>
                        )}
                        {["running", "paused"].includes(campaign.status) && (
                          <Button size="xs" variant="outline" onClick={() => act(campaign, "done")}
                            isLoading={busy === `done:${campaign._id}`}>Finish</Button>
                        )}
                      </Flex>
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        </TableContainer>
      )}

      {/* editor */}
      <Modal isOpen={isOpen} onClose={onClose} size="4xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent className="outreach-modal">
          <ModalHeader>{editing?._id ? "Edit campaign" : "New campaign"}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {editing && (
              <Stack spacing={4}>
                {editing.status && editing.status !== "draft" && (
                  <Text fontSize="sm" color="orange.400" textAlign="left">
                    This campaign has left draft — audience and steps are frozen because enrollments reference them.
                  </Text>
                )}
                <FormControl>
                  <FormLabel>Name</FormLabel>
                  <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
                </FormControl>

                <Divider />
                <Text fontWeight="bold" textAlign="left">Audience</Text>
                <Flex gap={3} wrap="wrap" align="flex-end">
                  <FormControl width="330px">
                    <FormLabel fontSize="sm">Who</FormLabel>
                    <Select value={editing.audience?.type || "prospects"}
                      isDisabled={editing.status && editing.status !== "draft"}
                      onChange={(e) => setEditing({ ...editing, audience: { ...editing.audience, type: e.target.value } })}>
                      <option value="prospects">Prospects (no subscription)</option>
                      <option value="customers">Customers</option>
                      <option value="single">One practice (a single prospect or customer)</option>
                    </Select>
                  </FormControl>
                  {editing.audience?.type === "single" && (
                    <Box flexBasis="100%">
                      {editing.audience?.single_snapshot?.business_name ? (
                        <Flex gap={2} align="center" wrap="wrap" mb={2}>
                          <Text fontSize="sm" fontWeight="semibold">{editing.audience.single_snapshot.business_name}</Text>
                          <Text fontSize="sm" color={mutedColor}>
                            {[editing.audience.single_snapshot.contact_name, editing.audience.single_snapshot.email].filter(Boolean).join(" · ")}
                          </Text>
                          <Badge colorScheme={editing.audience.single_snapshot.kind === "customer" ? "green" : "purple"}>
                            {editing.audience.single_snapshot.kind}
                          </Badge>
                          {!(editing.status && editing.status !== "draft") && (
                            <Button size="xs" variant="outline"
                              onClick={() => setEditing({ ...editing, audience: { ...editing.audience, single_bid: undefined, single_snapshot: undefined } })}>
                              Change
                            </Button>
                          )}
                        </Flex>
                      ) : (
                        <>
                          <Flex gap={2} mb={2}>
                            <Input size="sm" placeholder="Find the practice — name, contact, email or phone" value={singleQuery}
                              onChange={(e) => setSingleQuery(e.target.value)}
                              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); searchSingle(); } }} />
                            <Button size="sm" leftIcon={<FontAwesomeIcon icon={faMagnifyingGlass} />} onClick={searchSingle}
                              isLoading={singleSearching} isDisabled={singleQuery.trim().length < 2}>Search</Button>
                          </Flex>
                          {singleMatches && singleMatches.length === 0 && (
                            <Text fontSize="sm" color={mutedColor} textAlign="left">No practice with an admin contact matches.</Text>
                          )}
                          {singleMatches && singleMatches.length > 0 && (
                            <Stack spacing={1} borderWidth="1px" borderRadius="md" p={2} maxH="220px" overflowY="auto">
                              {singleMatches.map((contact) => (
                                <Flex key={contact.bid} gap={2} align="center" wrap="wrap" justify="space-between">
                                  <Box textAlign="left">
                                    <Text fontSize="sm" fontWeight="semibold" as="span">{contact.business_name || "—"}</Text>
                                    <Text fontSize="xs" color={mutedColor} as="span">
                                      {" "}· {[contact.contact_name, contact.email, contact.phone ? `cell ${contact.phone}` : "", contact.office_phone ? `office ${contact.office_phone}` : ""].filter(Boolean).join(" · ")}
                                    </Text>
                                    {" "}<Badge colorScheme={contact.kind === "customer" ? "green" : "purple"}>{contact.kind}</Badge>
                                    {contact.former_customer && <>{" "}<Badge colorScheme="orange">former customer</Badge></>}
                                  </Box>
                                  <Button size="xs" colorScheme="teal" onClick={() => pickSingle(contact)}>Use this practice</Button>
                                </Flex>
                              ))}
                            </Stack>
                          )}
                        </>
                      )}
                      <Text fontSize="xs" color={mutedColor} textAlign="left" mt={1}>
                        The whole campaign is for this one practice. Start enrolls them automatically; the sequence then
                        runs on schedule like any other campaign.
                      </Text>
                    </Box>
                  )}
                  {editing.audience?.type !== "single" && (<>
                  <FormControl width="180px">
                    <FormLabel fontSize="sm">Signed up after</FormLabel>
                    <Input type="date" value={editing.audience?.created_from ? String(editing.audience.created_from).slice(0, 10) : ""}
                      isDisabled={editing.status && editing.status !== "draft"}
                      onChange={(e) => setEditing({ ...editing, audience: { ...editing.audience, created_from: e.target.value || undefined } })} />
                  </FormControl>
                  <FormControl width="180px">
                    <FormLabel fontSize="sm">Signed up before</FormLabel>
                    <Input type="date" value={editing.audience?.created_to ? String(editing.audience.created_to).slice(0, 10) : ""}
                      isDisabled={editing.status && editing.status !== "draft"}
                      onChange={(e) => setEditing({ ...editing, audience: { ...editing.audience, created_to: e.target.value || undefined } })} />
                  </FormControl>
                  <Checkbox isChecked={!!editing.audience?.require_phone}
                    isDisabled={editing.status && editing.status !== "draft"}
                    onChange={(e) => setEditing({ ...editing, audience: { ...editing.audience, require_phone: e.target.checked } })}>
                    Has a mobile number
                  </Checkbox>
                  </>)}
                  <Button size="sm" onClick={previewAudience} isLoading={busy === "preview"}
                    isDisabled={editing.audience?.type === "single" && !editing.audience?.single_bid}>Preview</Button>
                </Flex>
                {preview && (
                  <Box borderWidth="1px" borderRadius="md" p={3}>
                    <Text fontSize="sm" fontWeight="bold" textAlign="left">{preview.count} contacts match</Text>
                    {preview.former_customers_excluded > 0 && (
                      <Text fontSize="xs" color="orange.500" textAlign="left">
                        {preview.former_customers_excluded} former customer{preview.former_customers_excluded === 1 ? "" : "s"} excluded (they cancelled — not prospects)
                      </Text>
                    )}
                    {(preview.sample || []).slice(0, 8).map((row) => (
                      <Text key={row.bid} fontSize="xs" color={mutedColor} textAlign="left">
                        {row.business_name} — {row.contact_name} · {row.email}{row.phone ? ` · cell ${row.phone}` : ""}{row.office_phone ? ` · office ${row.office_phone}` : ""} · {row.pms || "no PMS"} · {row.tz}
                      </Text>
                    ))}
                  </Box>
                )}

                <Divider />
                <Text fontWeight="bold" textAlign="left">Steps</Text>
                <StepEditor
                  steps={editing.steps || []}
                  disabled={!!(editing.status && editing.status !== "draft")}
                  onChange={(steps) => setEditing({ ...editing, steps })}
                />

                <Divider />
                <Flex gap={3} wrap="wrap" align="flex-end">
                  <FormControl width="260px">
                    <FormLabel fontSize="sm">Booking link ({"{{BookingLink}}"})</FormLabel>
                    <Input value={editing.booking_link || ""} onChange={(e) => setEditing({ ...editing, booking_link: e.target.value })} />
                  </FormControl>
                  {(editing.steps || []).some((s) => s.channel === "call") && (
                    <FormControl width="140px">
                      <FormLabel fontSize="sm">Call voice</FormLabel>
                      <Select value={editing.call_voice || "ara"} onChange={(e) => setEditing({ ...editing, call_voice: e.target.value })}>
                        {CALL_VOICES.map((v) => <option key={v} value={v}>{v}</option>)}
                      </Select>
                    </FormControl>
                  )}
                  <FormControl width="120px">
                    <FormLabel fontSize="sm">Quiet start</FormLabel>
                    <Input type="time" value={editing.quiet_hours?.start || "08:00"}
                      onChange={(e) => setEditing({ ...editing, quiet_hours: { ...editing.quiet_hours, start: e.target.value } })} />
                  </FormControl>
                  <FormControl width="120px">
                    <FormLabel fontSize="sm">Quiet end</FormLabel>
                    <Input type="time" value={editing.quiet_hours?.end || "21:00"}
                      onChange={(e) => setEditing({ ...editing, quiet_hours: { ...editing.quiet_hours, end: e.target.value } })} />
                  </FormControl>
                  <FormControl width="auto">
                    <FormLabel fontSize="sm">Frequency cap</FormLabel>
                    <Flex gap={2} align="center">
                      <Input type="number" min={0} step={1} width="90px" placeholder="0"
                        aria-label="max touches"
                        value={editing.frequency_cap?.max ?? ""}
                        onChange={(e) => setEditing({
                          ...editing,
                          frequency_cap: { days: 7, ...editing.frequency_cap, max: e.target.value === "" ? 0 : Math.max(0, parseInt(e.target.value, 10) || 0) },
                        })} />
                      <Text fontSize="sm" whiteSpace="nowrap">max touches per</Text>
                      <Input type="number" min={0} step={1} width="80px" placeholder="7"
                        aria-label="per days"
                        value={editing.frequency_cap?.days ?? ""}
                        onChange={(e) => setEditing({
                          ...editing,
                          frequency_cap: { max: 0, ...editing.frequency_cap, days: e.target.value === "" ? 0 : Math.max(0, parseInt(e.target.value, 10) || 0) },
                        })} />
                      <Text fontSize="sm">days</Text>
                    </Flex>
                  </FormControl>
                  <Checkbox isChecked={editing.stop_on_reply !== false}
                    onChange={(e) => setEditing({ ...editing, stop_on_reply: e.target.checked })}>
                    Stop on reply
                  </Checkbox>
                  <Checkbox isChecked={editing.stop_on_convert !== false}
                    onChange={(e) => setEditing({ ...editing, stop_on_convert: e.target.checked })}>
                    Stop when they become a customer
                  </Checkbox>
                </Flex>
                <Text fontSize="xs" color={mutedColor} textAlign="left">
                  Frequency cap: at most this many touches to the same practice across all campaigns in the window; extra
                  steps wait. Empty or 0 means no cap.
                </Text>
                <Text fontSize="xs" color={mutedColor} textAlign="left">
                  SMS and calls only go out between the quiet hours in each recipient&apos;s own timezone, and a step that
                  would exceed the frequency cap is deferred rather than skipped. Every email carries an unsubscribe link;
                  every first SMS says who we are and how to stop. Calls are held by the outreach voice agent, which is
                  separate from Ezra.
                </Text>
              </Stack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>Close</Button>
            <Button colorScheme="teal" onClick={saveCampaign} isLoading={busy === "save"}
              isDisabled={!!(editing?.status && editing.status !== "draft")}>Save</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* send to one contact */}
      <Modal isOpen={sendOneModal.isOpen} onClose={sendOneModal.onClose} size="3xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent className="outreach-modal">
          <ModalHeader>Send “{sendOneCampaign?.name}” to one contact</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontSize="sm" color={mutedColor} textAlign="left" mb={3}>
              Pick a single prospect or customer. Step 1 goes to them right now through the real engine (thread,
              suppression and merge fields included); the rest of the sequence follows only while the campaign is
              running. Quiet hours do not apply to this send.
              {sendOneCampaign?.status !== "running" && " This campaign is not running, so this is exactly one touch — ideal for a test."}
            </Text>
            <Flex gap={2} mb={3}>
              <Input size="sm" placeholder="Practice name, contact name, email or phone" value={contactQuery}
                onChange={(e) => setContactQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") searchContacts(); }} autoFocus />
              <Button size="sm" leftIcon={<FontAwesomeIcon icon={faMagnifyingGlass} />} onClick={searchContacts}
                isLoading={searching} isDisabled={contactQuery.trim().length < 2}>Search</Button>
            </Flex>
            {contacts && contacts.length === 0 && (
              <Text fontSize="sm" color={mutedColor} textAlign="left">No practice with an admin contact matches.</Text>
            )}
            {contacts && contacts.length > 0 && (
              <TableContainer borderWidth="1px" borderRadius="md" whiteSpace="normal">
                <Table size="sm" variant="simple">
                  <Thead><Tr><Th>Practice</Th><Th>Contact</Th><Th>Kind</Th><Th>In this campaign</Th><Th></Th></Tr></Thead>
                  <Tbody>
                    {contacts.map((contact) => {
                      const enrolled = contact.enrollment;
                      return (
                        <Tr key={contact.bid}>
                          <Td>
                            <Text fontSize="sm" fontWeight="semibold">{contact.business_name || "—"}</Text>
                            <Text fontSize="xs" color={mutedColor}>{[contact.pms || "no PMS", contact.tz].join(" · ")}</Text>
                          </Td>
                          <Td>
                            <Text fontSize="sm">{contact.contact_name || "—"}</Text>
                            <Text fontSize="xs" color={mutedColor}>
                              {contact.email}{contact.phone ? ` · cell ${contact.phone}` : ""}{contact.office_phone ? ` · office ${contact.office_phone}` : ""}
                            </Text>
                          </Td>
                          <Td>
                            <Flex gap={1} wrap="wrap">
                              <Badge colorScheme={contact.kind === "customer" ? "green" : "purple"}>{contact.kind}</Badge>
                              {contact.former_customer && <Badge colorScheme="orange">former customer</Badge>}
                              {contact.partner_owned && <Badge>partner-owned</Badge>}
                              {contact.inactive && <Badge colorScheme="red">inactive</Badge>}
                            </Flex>
                          </Td>
                          <Td>
                            {enrolled
                              ? <Text fontSize="xs">{enrolled.status} · step {enrolled.current_step}{enrolled.source === "manual" ? " · manual" : ""}</Text>
                              : <Text fontSize="xs" color={mutedColor}>not enrolled</Text>}
                          </Td>
                          <Td whiteSpace="nowrap">
                            <Flex gap={1} wrap="wrap" justify="flex-end">
                              {enrolled ? (
                                <Button size="xs" colorScheme="orange" variant="outline"
                                  isLoading={busy === `sendone:${contact.bid}`}
                                  onClick={() => { if (window.confirm(`Restart ${contact.business_name}'s sequence from step 1 and send now?`)) sendToContact(contact, true); }}>
                                  Restart &amp; send
                                </Button>
                              ) : (
                                <Button size="xs" colorScheme="teal" isLoading={busy === `sendone:${contact.bid}`}
                                  onClick={() => sendToContact(contact, false)}>
                                  Send now
                                </Button>
                              )}
                            </Flex>
                          </Td>
                        </Tr>
                      );
                    })}
                  </Tbody>
                </Table>
              </TableContainer>
            )}
            {lastSend && !lastSend.already_enrolled && (
              <Box mt={3} borderWidth="1px" borderRadius="md" p={3}>
                <Text fontSize="sm" fontWeight="bold" textAlign="left">
                  {lastSend.contact.business_name} — step 1 {lastSend.step?.result || lastSend.outcome}
                  {lastSend.step?.channel ? ` by ${lastSend.step.channel}` : ""}
                </Text>
                {lastSend.step?.detail && <Text fontSize="xs" color="red.400" textAlign="left">{lastSend.step.detail}</Text>}
                <Text fontSize="xs" color={mutedColor} textAlign="left">
                  Sequence {lastSend.status}
                  {lastSend.next_due_at ? ` · next step due ${moment(lastSend.next_due_at).format("MMM D, h:mm a")}` : ""}
                  {lastSend.note ? ` · ${lastSend.note}` : ""}
                  {lastSend.thread_id ? " · the conversation is in the Outreach Inbox" : ""}
                </Text>
              </Box>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" onClick={sendOneModal.onClose}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* dry run */}
      <Modal isOpen={dryRunModal.isOpen} onClose={dryRunModal.onClose} size="4xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent className="outreach-modal">
          <ModalHeader>Dry run — rendered messages, nothing sent</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {dryRun && (
              <Stack spacing={4}>
                <Text fontSize="sm" color={mutedColor} textAlign="left">
                  Showing {dryRun.rendered?.length || 0} of {dryRun.of} enrollments, exactly as the engine will send them.
                </Text>
                {dryRun.dry_run && (
                  <Text fontSize="sm" color="green.500" textAlign="left">
                    This dry run is recorded; the campaign can now be started as long as the steps are not edited again.
                  </Text>
                )}
                {(dryRun.rendered || []).map((row) => (
                  <Box key={row.enrollment_id} borderWidth="1px" borderRadius="md" p={3}>
                    <Text fontSize="sm" fontWeight="bold" textAlign="left">
                      {row.to?.business} · {row.to?.email}{row.to?.phone ? ` · ${row.to.phone}` : ""}
                    </Text>
                    {(row.steps || []).map((step) => (
                      <Box key={step.step} mt={2}>
                        <Badge>{step.channel} · step {step.step + 1}</Badge>
                        {step.error ? (
                          <Text fontSize="sm" color="red.400" textAlign="left">{step.error}</Text>
                        ) : step.channel === "call" ? (
                          <>
                            <Text fontSize="xs" color={mutedColor} textAlign="left">
                              AI call to {CALL_TARGET_LABELS[step.call_target] || step.call_target}{step.to ? ` (${step.to})` : " — no number on file"}
                            </Text>
                            <Text fontSize="sm" fontWeight="semibold" textAlign="left">Goal</Text>
                            <Text className="outreach-message-body" fontSize="sm">{step.goal}</Text>
                            {step.guidelines && (
                              <>
                                <Text fontSize="sm" fontWeight="semibold" textAlign="left" mt={1}>Guidelines</Text>
                                <Text className="outreach-message-body" fontSize="sm">{step.guidelines}</Text>
                              </>
                            )}
                          </>
                        ) : (
                          <>
                            {step.subject && <Text fontSize="sm" fontWeight="semibold" textAlign="left">{step.subject}</Text>}
                            <Text className="outreach-message-body" fontSize="sm">{step.body}</Text>
                          </>
                        )}
                      </Box>
                    ))}
                  </Box>
                ))}
              </Stack>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* per-step metrics */}
      <Modal isOpen={metricsModal.isOpen} onClose={metricsModal.onClose} size="4xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent className="outreach-modal">
          <ModalHeader>Metrics — {metrics?.name}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {metrics && (() => {
              const byStatus = metrics.metrics?.by_status || {};
              const byStep = metrics.metrics?.by_step || {};
              const steps = metrics.steps || [];
              // Any step index the API reports but the campaign no longer lists still gets a row.
              const stepIndexes = Array.from(new Set([
                ...steps.map((_, i) => i),
                ...Object.keys(byStep).map((k) => Number(k)).filter((n) => Number.isInteger(n) && n >= 0),
              ])).sort((a, b) => a - b);
              const cell = (n) => (n ? n : "—");
              return (
                <Stack spacing={4}>
                  <Flex gap={2} wrap="wrap" align="center">
                    <Text fontSize="sm" color={mutedColor}>Enrollments:</Text>
                    {STATUS_METRIC_KEYS.map((key) => (
                      <Badge key={key} colorScheme={byStatus[key] ? (key === "opted_out" || key === "failed" ? "red" : key === "converted" ? "green" : "blue") : "gray"} variant="subtle">
                        {key.replace(/_/g, " ")} {byStatus[key] || 0}
                      </Badge>
                    ))}
                  </Flex>
                  <TableContainer borderWidth="1px" borderRadius="md">
                    <Table size="sm" variant="simple">
                      <Thead>
                        <Tr>
                          <Th>Step</Th>
                          <Th>Channel</Th>
                          {STEP_METRIC_COLUMNS.map(([key, label]) => <Th key={key} isNumeric>{label}</Th>)}
                        </Tr>
                      </Thead>
                      <Tbody>
                        {stepIndexes.length === 0 ? (
                          <Tr><Td colSpan={2 + STEP_METRIC_COLUMNS.length}><Text fontSize="sm" color={mutedColor}>No steps have run yet.</Text></Td></Tr>
                        ) : stepIndexes.map((i) => {
                          const row = byStep[String(i)] || {};
                          return (
                            <Tr key={i}>
                              <Td>{i + 1}</Td>
                              <Td>{steps[i]?.channel || "—"}</Td>
                              {STEP_METRIC_COLUMNS.map(([key]) => <Td key={key} isNumeric>{cell(row[key])}</Td>)}
                            </Tr>
                          );
                        })}
                      </Tbody>
                    </Table>
                  </TableContainer>
                  <Text fontSize="xs" color={mutedColor} textAlign="left">
                    Delivered comes from the provider&apos;s status callbacks. Replied, opted out and converted count the
                    enrollments whose sequence stopped with that outcome after this step.
                  </Text>
                </Stack>
              );
            })()}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" onClick={metricsModal.onClose}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* sending identity */}
      <Modal isOpen={identityModal.isOpen} onClose={identityModal.onClose} size="lg" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent className="outreach-modal">
          <ModalHeader>Outreach sending identity</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {identityLoading && !identity ? (
              <Flex justify="center" py={6}><Spinner color="blue.500" /></Flex>
            ) : identity && (
              <Stack spacing={4}>
                {IDENTITY_FIELDS.map(({ key, label, placeholder, maxLength, help }) => (
                  <FormControl key={key}>
                    <FormLabel fontSize="sm" mb={1}>
                      {label}
                      {!identity.stored?.[key] && identity[key] && (
                        <Badge ml={2} colorScheme="gray" variant="subtle" fontSize="2xs" verticalAlign="middle">from environment</Badge>
                      )}
                    </FormLabel>
                    <Input size="sm" value={identityForm[key]} placeholder={placeholder} maxLength={maxLength}
                      onChange={(e) => setIdentityForm({ ...identityForm, [key]: e.target.value })} />
                    <FormHelperText fontSize="xs" textAlign="left" mt={1}>{help}</FormHelperText>
                  </FormControl>
                ))}
                {identity.channels && (
                  <Flex gap={2} wrap="wrap" align="center" fontSize="xs">
                    <Text color={mutedColor}>Channels:</Text>
                    <Badge colorScheme={identity.channels.sms?.enabled ? "green" : "red"}>
                      SMS {identity.channels.sms?.enabled ? "on" : "off"}
                    </Badge>
                    <Badge colorScheme={identity.channels.call?.enabled ? "green" : "red"}>
                      AI calls {identity.channels.call?.enabled ? "on" : (identity.channels.call?.voice_agent ? "off" : "voice agent key missing")}
                    </Badge>
                    <Badge colorScheme={identity.channels.email?.enabled ? "green" : "red"} whiteSpace="normal">
                      Email {identity.channels.email?.enabled
                        ? (identity.channels.email.domain || "on")
                        : (identity.channels.email?.disabled_reason || "off")}
                    </Badge>
                  </Flex>
                )}
                <Text fontSize="xs" color={mutedColor} textAlign="left">
                  An empty field clears the stored value; the OUTREACH_* environment variable, if any, then applies.
                  {identity.updated ? ` Last saved ${moment(identity.updated).format("MMM D, h:mm a")}.` : ""}
                </Text>
              </Stack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={identityModal.onClose}>Close</Button>
            <Button colorScheme="teal" onClick={saveIdentity} isLoading={busy === "identity"} isDisabled={!identity}>Save</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

export default OutreachCampaigns;
